// src/tabs/logements/TaxeCrosscheckPanel.jsx — ADR-280 (crosscheck taxe
// de séjour Booking, v1 sans état).
//
// Panneau repliable (défaut REPLIÉ) monté sous le résumé barème de
// TaxeReminderPanel (carte Tarification), UNIQUEMENT si un barème valide
// existe. Le fetch part AU CLIC sur « Vérifier avec Booking » — jamais au
// rendu ni à l'ouverture du panneau.
//
// Lecture des verdicts (règle « état actuel ») : Booking calcule la taxe sur
// l'assiette hors TVA (10 % déduite) → un collecté ≈ notre calcul ÷ 1,1 est
// NORMAL et s'affiche « cohérent (TVA Booking déduite) » — jamais comme une
// erreur. La réponse serveur ne contient AUCUNE PII (dates/montants/ids).

import { useState } from "react";
import { DS } from "../../lib/ui";
import { FicheAlert, SecondaryButton } from "./_ficheUi";
import { crosscheckTaxeBareme } from "../../lib/taxe-api";

const VERDICT_LABELS = {
  coherent_tva_deduite: "cohérent (TVA Booking déduite)",
  coherent_brut: "cohérent (montant brut)",
  ecart: "écart",
  non_comparable: "non comparable",
};

const RAISON_LABELS = {
  assiette_absente: "assiette inexploitable",
  guests_absents: "occupants inconnus",
  bareme_non_valide: "barème invalide",
  nuits_invalides: "dates invalides",
  expected_zero: "aucun assujetti calculable",
};

function eur(n) {
  return Number.isFinite(Number(n)) ? `${Number(n).toFixed(2)} €` : "—";
}

function fmtDates(item) {
  const d = (iso) => (iso ? String(iso).slice(0, 10) : "?");
  return `${d(item.start_date)} → ${d(item.end_date)}`;
}

const th = {
  textAlign: "left",
  padding: "4px 8px",
  fontSize: 10.5,
  fontWeight: 700,
  color: DS.textMuted,
  textTransform: "uppercase",
  letterSpacing: 0.3,
  borderBottom: `1px solid ${DS.border || "#eef1f5"}`,
  whiteSpace: "nowrap",
};
const td = {
  padding: "5px 8px",
  fontSize: 12,
  color: DS.marine,
  borderBottom: "1px solid #f2f4f8",
  whiteSpace: "nowrap",
};

export default function TaxeCrosscheckPanel({ logementId, bareme }) {
  const [open, setOpen] = useState(false); // défaut REPLIÉ
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // SEUL déclencheur du fetch : le clic sur « Vérifier avec Booking ».
  async function runCheck() {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const out = await crosscheckTaxeBareme({ logement_id: logementId, bareme });
      setResult(out);
    } catch (e) {
      console.error("[TaxeCrosscheckPanel] crosscheck", e);
      setError(e.message || "Vérification Booking échouée");
    } finally {
      setLoading(false);
    }
  }

  const stats = result?.stats || null;
  const comparables = stats ? stats.total - stats.non_comparables : 0;
  const coherentes = stats ? stats.coherent_tva_deduite + stats.coherent_brut : 0;

  return (
    <div style={{ marginTop: 10 }} data-testid="taxe-crosscheck-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="taxe-crosscheck-toggle"
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          fontSize: 12,
          fontWeight: 600,
          color: DS.marine,
          fontFamily: DS.fontBody,
        }}
      >
        {open ? "▾" : "▸"} Vérifier le barème avec Booking
      </button>

      {open && (
        <div style={{ marginTop: 8 }}>
          {/* Texte d'aide = état actuel : l'écart ÷ 1,1 est NORMAL. */}
          <div style={{ fontSize: 11.5, color: DS.textMuted, lineHeight: 1.5, marginBottom: 8 }}>
            Compare la taxe <strong>collectée par Booking</strong> (résas des 12 derniers mois)
            au calcul de ce barème. Booking calcule sur l'assiette hors TVA (10 % déduite) :
            un montant collecté ≈ notre calcul ÷ 1,1 est <strong>normal</strong> et s'affiche
            « cohérent (TVA Booking déduite) ».
          </div>

          <SecondaryButton onClick={runCheck} disabled={loading} style={{ fontSize: 12 }}>
            {loading ? "Vérification…" : "Vérifier avec Booking"}
          </SecondaryButton>

          {error && (
            <FicheAlert tone="danger" style={{ marginTop: 8 }} testId="taxe-crosscheck-error">
              {error}
            </FicheAlert>
          )}

          {result && !error && (
            <div style={{ marginTop: 8 }}>
              {result.verdict_global === "coherent" && (
                <FicheAlert tone="success" testId="taxe-crosscheck-badge">
                  ✓ {coherentes}/{comparables} résa{comparables > 1 ? "s" : ""} Booking
                  cohérente{coherentes > 1 ? "s" : ""} avec ce barème.
                </FicheAlert>
              )}
              {result.verdict_global === "a_verifier" && (
                <FicheAlert tone="warning" testId="taxe-crosscheck-badge">
                  ⚠ {stats.ecarts} écart{stats.ecarts > 1 ? "s" : ""} sur {comparables} résa
                  {comparables > 1 ? "s" : ""} comparable{comparables > 1 ? "s" : ""} — à
                  vérifier (barème, occupants ou frais inclus).
                </FicheAlert>
              )}
              {result.verdict_global === "aucune_resa_comparable" && (
                <FicheAlert tone="info" testId="taxe-crosscheck-badge">
                  Aucune résa Booking avec taxe collectée sur les 12 derniers mois.
                  {stats.zero_tax_count > 0 && (
                    <>
                      {" "}
                      ({stats.zero_tax_count} résa{stats.zero_tax_count > 1 ? "s" : ""} Booking
                      sans taxe collectée — Booking ne collecte peut-être pas la taxe sur ce
                      logement.)
                    </>
                  )}
                </FicheAlert>
              )}

              {result.resas && result.resas.length > 0 && (
                <div style={{ overflowX: "auto", marginTop: 8 }}>
                  <table
                    data-testid="taxe-crosscheck-table"
                    style={{ borderCollapse: "collapse", width: "100%" }}
                  >
                    <thead>
                      <tr>
                        <th style={th}>Dates</th>
                        <th style={th}>Nuits</th>
                        <th style={th}>Collecté</th>
                        <th style={th}>Attendu (TVA déduite)</th>
                        <th style={th}>Verdict</th>
                        <th style={th}>Écart</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.resas.map((item) => (
                        <tr key={item.id || fmtDates(item)}>
                          <td style={td}>{fmtDates(item)}</td>
                          <td style={td}>{item.nuits ?? "—"}</td>
                          <td style={td}>{eur(item.collected)}</td>
                          <td style={td}>{eur(item.expected_tva_deduite)}</td>
                          <td
                            style={{
                              ...td,
                              color:
                                item.verdict === "ecart"
                                  ? DS.warningText
                                  : item.verdict === "non_comparable"
                                    ? DS.textMuted
                                    : DS.successText,
                            }}
                          >
                            {VERDICT_LABELS[item.verdict] || item.verdict}
                            {item.verdict === "non_comparable" && item.raison
                              ? ` (${RAISON_LABELS[item.raison] || item.raison})`
                              : ""}
                          </td>
                          <td style={td}>
                            {Number.isFinite(Number(item.ecart_pct))
                              ? `${item.ecart_pct > 0 ? "+" : ""}${item.ecart_pct} %`
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
